import {
  Button,
  Circle,
  HStack,
  Heading,
  Icon,
  Link,
  SimpleGrid,
  Text,
  VStack,
} from "@chakra-ui/react";
import { IBooking, IUser } from "../types/types";
import { PiArrowUpRight, PiScooter, PiSuitcaseRolling } from "react-icons/pi";
import React, { useContext, useEffect, useState } from "react";

import BookingCard from "../components/BookingCard";
import { BookingService } from "../services/booking.service";
import { ExternalLinkIcon } from "@chakra-ui/icons";
import { GiHouseKeys } from "react-icons/gi";
import { Helmet } from "react-helmet";
import Spinner from "../components/Spinner";
import { UserContext } from "../App";

const bookingService = new BookingService();

const Home: React.FC = () => {
  const { user } = useContext(UserContext);

  const [pastBookings, setPastBookings] = useState<IBooking[]>();
  const [activeBookings, setActiveBookings] = useState<IBooking[]>();
  const [bookingsLoading, setBookingsLoading] = useState<boolean>(false);

  useEffect(() => {
    getBookings();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getBookings = async () => {
    setBookingsLoading(true);

    bookingService
      .list()
      .then(({ data }) => {
        console.log("[getBookingData] bookings", data);

        if (data && data.data) {
          const active_bookings = data.data.filter(
            (b) => b.status === "active"
          );
          setActiveBookings(active_bookings);

          const past_bookings = data.data.filter((b) => b.status !== "active");
          setPastBookings(past_bookings);
        }
      })
      .catch((error) => {
        console.error("[getBookings] error", error);
      })
      .then(() => {
        setBookingsLoading(false);
      });
  };

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Inicio - SALA</title>
        <link rel="canonical" href={window.location.href} />
      </Helmet>
      <VStack w={"full"} spacing={8} alignItems={"start"} p={4}>
        {bookingsLoading ? (
          <Spinner translucent />
        ) : (
          <>
            <Heading as="h1" size="xl">
              ¡Hola{", " + (user as IUser).sala_user.first_name || ""}!
              {activeBookings && activeBookings.length > 0
                ? "¡Tienes una reserva activa!"
                : "¿Qué necesitas guardar?"}
            </Heading>

            {activeBookings &&
              activeBookings.length > 0 &&
              activeBookings.map((booking) => (
                <BookingCard
                  key={booking.id}
                  booking={booking}
                  onSuccess={() => getBookings()}
                />
              ))}

            <VStack
              spacing={2}
              justifyContent={"start"}
              alignItems={"start"}
              w={"full"}
              h={"fit-content"}
              textAlign={"start"}
            >
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w={"full"}>
                {[
                  {
                    id: "luggage",
                    title: "Maletas",
                    description:
                      "Maletas de mano, mochilas grandes, ¡tamaño a medida!",
                    icon: PiSuitcaseRolling,
                  },
                  {
                    id: "belongings",
                    title: "Pertenencias",
                    description:
                      "Guarda tus pertenencias más valiosas en un pequeño espacio seguro",
                    icon: GiHouseKeys,
                  },
                  {
                    id: "scooter",
                    title: "Patinetes",
                    description:
                      "Guarda y carga tu patinetes eléctricos en nuestras taquillas",
                    icon: PiScooter,
                  },
                ].map((item) => (
                  <Button
                    key={item.id}
                    bgColor={"gray.200"}
                    borderRadius={"2xl"}
                    h={"fit-content"}
                    w={"full"}
                    p={4}
                    alignItems={"start"}
                    _hover={{
                      bgColor: "gray.300",
                      shadow: "md",
                    }}
                    isDisabled={
                      bookingsLoading ||
                      (activeBookings && activeBookings.length > 0)
                    }
                    as={
                      bookingsLoading ||
                      (activeBookings && activeBookings.length > 0)
                        ? undefined
                        : Link
                    }
                    href={`/booking?usage=${item.id}`}
                  >
                    <HStack spacing={4} alignItems={"start"} w={"full"}>
                      <HStack spacing={4} alignItems={"start"} w={"full"}>
                        <Circle size={10} bg={"gray.50"}>
                          <Icon as={item.icon} boxSize={"50%"} />
                        </Circle>
                        <VStack
                          spacing={2}
                          justifyContent={"start"}
                          alignItems={"start"}
                          textAlign={"start"}
                        >
                          <Text fontWeight={"medium"}>{item.title}</Text>

                          <Text
                            fontSize="15px"
                            fontWeight={"normal"}
                            color={"gray.600"}
                            whiteSpace={"normal"}
                            style={{
                              wordWrap: "break-word",
                            }}
                            flex={1}
                          >
                            {item.description}
                          </Text>
                        </VStack>
                      </HStack>
                      <Circle size={10} bg={"gray.300"} justifySelf={"end"}>
                        <Icon as={PiArrowUpRight} boxSize={"60%"} />
                      </Circle>
                    </HStack>
                  </Button>
                ))}
              </SimpleGrid>
            </VStack>

            <VStack spacing={4} alignItems={"start"} w={"full"}>
              <HStack
                justifyContent={"space-between"}
                alignItems={"end"}
                w={"full"}
              >
                <Heading as="h2" size="lg" lineHeight={1}>
                  Tus últimas reservas
                </Heading>

                <Link
                  href="/bookings"
                  color="teal.500"
                  fontWeight={"semibold"}
                  textDecoration={"underline"}
                >
                  Ver todas <ExternalLinkIcon />
                </Link>
              </HStack>
              {pastBookings && pastBookings.length > 0 ? (
                <VStack
                  spacing={4}
                  justifyContent={"start"}
                  alignItems={"end"}
                  w={"full"}
                >
                  {pastBookings
                    .sort(
                      (a, b) =>
                        new Date(b.start_time).getTime() -
                        new Date(a.start_time).getTime()
                    )
                    .slice(0, 3)
                    .map((booking) => (
                      <BookingCard
                        key={booking.id}
                        booking={booking}
                        onSuccess={() => getBookings()}
                      />
                    ))}
                </VStack>
              ) : (
                <Text color={"gray.600"} fontSize={"lg"}>
                  Todavía no has hecho ninguna reserva. ¿A qué esperas? ¡Haz una
                  ahora!
                </Text>
              )}
            </VStack>
          </>
        )}
      </VStack>
    </>
  );
};

export default Home;
